// Файл FileViewer.js

/**
 * Файл реализует логику модуля «FileViewer» в разделе
 * «DocumentAttachments» интерфейса.
 * В нём собраны связанные обработчики и вспомогательные операции для этой
 * части приложения.
 * Код взаимодействует с соседними компонентами и передаёт им
 * подготовленные данные без дублирования общей логики.
 * Такое разделение упрощает сопровождение, проверку поведения и дальнейшее
 * расширение функциональности.
 */

//
// Выборщик просмотрщиков. По расширению файла выбирает нужный
// компонент-просмотрщик, сам загружает содержимое файла (loadBlob),
// показывает индикатор загрузки и сообщение об ошибке, затем
// передаёт Blob конкретному просмотрщику.
//
// Новые форматы (pptx, txt, csv …) добавляются одной строкой
// в VIEWER_REGISTRY — плагинная архитектура.

import { useEffect, useRef, useState } from 'react';

import DocxViewer from './DocxViewer';
import DocViewer from './DocViewer';
import ExcelViewer from './ExcelViewer';
import ImageViewer from './ImageViewer';
import CsvViewer from './CsvViewer';
import LoadingIndicator from '../../shared_code/loading';



const VIEWER_REGISTRY = {
    docx: DocxViewer,
    doc: DocViewer,
    xlsx: ExcelViewer,
    xls: ExcelViewer,
    csv: CsvViewer,
    png: ImageViewer,
    jpg: ImageViewer,
    jpeg: ImageViewer,
    gif: ImageViewer,
    webp: ImageViewer,
    bmp: ImageViewer,
    svg: ImageViewer,
};


// Умеет ли этот модуль показывать данный формат в панели.
export const isViewableInPanel = (ext) =>
    Object.prototype.hasOwnProperty.call(
        VIEWER_REGISTRY,
        String(ext || '').toLowerCase()
    );


const FileViewer = ({ fileName, ext, loadBlob }) => {
    const normalizedExt = String(ext || '').toLowerCase();
    const Viewer = VIEWER_REGISTRY[normalizedExt];

    // Держим loadBlob в ref, чтобы новая ссылка на функцию
    // (создаётся на каждом рендере родителя) не перезапускала
    // эффект и не уводила в бесконечную перезагрузку.
    const loadBlobRef = useRef(loadBlob);
    loadBlobRef.current = loadBlob;

    const [blob, setBlob] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!Viewer || typeof loadBlobRef.current !== 'function')
            return undefined;

        let cancelled = false;

        setIsLoading(true);
        setError('');
        setBlob(null);

        Promise.resolve()
            .then(() => loadBlobRef.current())
            .then((result) => {
                if (cancelled)
                    return;

                setBlob(result);
                setIsLoading(false);
            })
            .catch(() => {
                if (cancelled)
                    return;

                setError('Не удалось загрузить файл');
                setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [Viewer, fileName, normalizedExt]);

    if (!Viewer) {
        return (
            <div
                className={
                    'file-viewer__message flex-column flex-align-center '
                    + 'file-viewer__message--error'
                }
            >
                Формат «.{normalizedExt}» пока не поддерживается
                для просмотра.
            </div>
        );
    }

    if (isLoading) {
        return (
            <div
                className="file-viewer__message flex-column flex-align-center"
            >
                <LoadingIndicator label="Ожидание файла" />
            </div>
        );
    }

    if (error) {
        return (
            <div
                className={
                    'file-viewer__message flex-column flex-align-center '
                    + 'file-viewer__message--error'
                }
            >
                {error}
            </div>
        );
    }

    return (
        <Viewer
            blob={blob}
            fileName={fileName}
            ext={normalizedExt}
        />
    );
};


export default FileViewer;
